import './laxhjalpen-user-participation-page.scss';
import { Tooltip } from '@mui/material';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BizUnitSelect from '../../../../../FrontEndReact/src/Core/Components/Mini/BizUnitSelect';
import { BizSelect } from '../../../../../FrontEndReact/src/Core/Components/Mini/BizSelect';
import { DashboardPeriod, getDateRangeForPeriod } from '../../../../../FrontEndReact/src/Core/Types/admin/dashboard';
import {
	formatToSimpleDateString,
	formatToTodayString,
	parseStringToDate,
} from '../../../../../FrontEndReact/src/Core/Utils/dateUtils';
import { LhUserParticipationStats, useGetUserParticipationStats } from '../Services/laxhjalpenUserParticipation';

type ParticipationSummary = {
	totalParticipations: number;
	attendedCount: number;
	approvedAbsenceCount: number;
	notApprovedAbsenceCount: number;
	attendancePercent: number;
	approvedAbsencePercent: number;
	notApprovedAbsencePercent: number;
	uniqueEvents: number;
	uniqueEventsWApprovedAbsence: number;
	uniqueEventsWNotApprovedAbsence: number;
	usersWithData: number;
};

type PeriodOption = {
	Id: DashboardPeriod;
	Name: string;
	TranslationKey: string;
};

type UserRow = LhUserParticipationStats & {
	attendancePercent: number;
	approvedAbsencePercent: number;
	notApprovedAbsencePercent: number;
	status: 'high' | 'stable' | 'follow-up';
};

const periodOptions: PeriodOption[] = [
	{
		Id: 'CURRENT_MONTH',
		Name: 'Current Month',
		TranslationKey: 'STATISTICS_DASHBOARD.PERIODS.CURRENT_MONTH',
	},
	{
		Id: 'LAST_MONTH',
		Name: 'Last Month',
		TranslationKey: 'STATISTICS_DASHBOARD.PERIODS.LAST_MONTH',
	},
	{
		Id: 'CURRENT_YEAR',
		Name: 'Current Year',
		TranslationKey: 'STATISTICS_DASHBOARD.PERIODS.CURRENT_YEAR',
	},
	{
		Id: 'LAST_YEAR',
		Name: 'Last Year',
		TranslationKey: 'STATISTICS_DASHBOARD.PERIODS.LAST_YEAR',
	},
];

const LaxhjalpenUserParticipationPage = () => {
	const { t } = useTranslation();
	const [unitId, setUnitId] = useState<number | undefined>(undefined);
	const [period, setPeriod] = useState<DashboardPeriod>('CURRENT_MONTH');
	const {
		data,
		from,
		to,
		statusState,
		uniqueEvents,
		uniqueEventsWApprovedAbsence,
		uniqueEventsWNotApprovedAbsence,
		lastUpdated,
	} = useGetUserParticipationStats(unitId, period);

	const rows = data.map(mapRow).sort((left, right) => right.attendancePercent - left.attendancePercent);
	const summary = summarizeRows(data, uniqueEvents, uniqueEventsWApprovedAbsence, uniqueEventsWNotApprovedAbsence);
	const lastUpdatedLabel = getLastUpdatedLabel(lastUpdated, t);
	const overviewHelpContent = (
		<div className='biz-lh-user-participation-page__tooltip'>
			<strong>{t('LAXHJALPEN.USER_PARTICIPATION.HELP.TITLE', { defaultValue: 'Så räknas översikten' })}</strong>
			<p>
				{t('LAXHJALPEN.USER_PARTICIPATION.HELP.INTRO', {
					defaultValue:
						'Procentsiffrorna i översikten bygger på summan av alla elevrader i den valda perioden och enheten. Samma pass kan därför räknas flera gånger här om flera elever deltog.',
				})}
			</p>
			<ul>
				<li>
					{t('LAXHJALPEN.USER_PARTICIPATION.HELP.ATTENDANCE', {
						defaultValue: 'Närvaro: {{count}} av {{total}} deltagartillfällen.',
						count: summary.attendedCount,
						total: summary.totalParticipations,
					})}
				</li>
				<li>
					{t('LAXHJALPEN.USER_PARTICIPATION.HELP.APPROVED', {
						defaultValue: 'Giltig frånvaro: {{count}} av {{total}} deltagartillfällen.',
						count: summary.approvedAbsenceCount,
						total: summary.totalParticipations,
					})}
				</li>
				<li>
					{t('LAXHJALPEN.USER_PARTICIPATION.HELP.NOT_APPROVED', {
						defaultValue: 'Ogiltig frånvaro: {{count}} av {{total}} deltagartillfällen.',
						count: summary.notApprovedAbsenceCount,
						total: summary.totalParticipations,
					})}
				</li>
				<li>
					{t('LAXHJALPEN.USER_PARTICIPATION.HELP.UNIQUE_EVENTS', {
						defaultValue:
							'Korten högst upp visar unika pass längst ned: {{uniqueEvents}} totalt, {{approved}} med giltig frånvaro och {{notApproved}} med ogiltig frånvaro.',
						uniqueEvents: summary.uniqueEvents,
						approved: summary.uniqueEventsWApprovedAbsence,
						notApproved: summary.uniqueEventsWNotApprovedAbsence,
					})}
				</li>
			</ul>
		</div>
	);

	const isAdminContext = typeof window !== 'undefined' && window.location.href.indexOf('/admin') >= 0;

	const adminPageWrapperMarkup = (content: ReactNode) => {
		return (
			<div className='portlet box admin-primary'>
				<div className='portlet-title'>
					<div className='caption'>{t('LAXHJALPEN.USER_PARTICIPATION.TITLE', { defaultValue: 'Närvaroöversikt' })}</div>
				</div>
				<div className='admin-content'>
					<div className='admin-panel'>
						{/* <div className='admin-content'>{content}</div> */}
						{content}
					</div>
				</div>
			</div>
		);
	};

	const pageContent = (
		<div className='biz-lh-user-participation-page'>
			<div className='biz-lh-user-participation-page__header'>
				<div>
					<h1 className='biz-lh-user-participation-page__title'>
						{t('LAXHJALPEN.USER_PARTICIPATION.TITLE', { defaultValue: 'Närvaroöversikt' })}
					</h1>
					<p className='biz-lh-user-participation-page__subtitle'>
						{t('LAXHJALPEN.USER_PARTICIPATION.SUBTITLE', {
							defaultValue: 'Följ upp närvaro, frånvaro och deltagande per elev.',
						})}
					</p>
				</div>
				<div className='biz-lh-user-participation-page__date-range'>{formatDateRange(from, to)}</div>
			</div>

			<div className='biz-lh-user-participation-page__filters'>
				<label className='biz-lh-user-participation-page__filter biz-lh-user-participation-page__filter--unit'>
					<span>{t('STATISTICS_DASHBOARD.UNIT', { defaultValue: 'Enhet' })}</span>
					<BizUnitSelect
						selectedId={unitId}
						onChange={(unit) => setUnitId(unit?.Id ? Number(unit.Id) : undefined)}
						accessTypeKey='EVENT'
						noSelectText={t('LAXHJALPEN.USER_PARTICIPATION.ALL_UNITS', { defaultValue: '-- Alla enheter --' })}
						childNoSelectText={t('LAXHJALPEN.USER_PARTICIPATION.ALL_UNITS', { defaultValue: '-- Alla enheter --' })}
						direction='horizontal'
					/>
				</label>
				<label className='biz-lh-user-participation-page__filter'>
					<span>{t('STATISTICS_DASHBOARD.PERIOD', { defaultValue: 'Period' })}</span>
					<BizSelect
						className='form-control'
						options={periodOptions.map((option) => ({
							...option,
							Name: option.TranslationKey ? t(option.TranslationKey) : option.Name,
						}))}
						selectedId={period}
						onChange={(option) => setPeriod((option?.Id as DashboardPeriod) ?? 'CURRENT_MONTH')}
						noSelectText={t('STATISTICS_DASHBOARD.SELECT_PERIOD', { defaultValue: '-- Välj period --' })}
						getDescription={(option) => describePeriod(option.Id as DashboardPeriod)}
					/>
				</label>
			</div>

			{statusState === 'LOADING' && (
				<div className='biz-lh-user-participation-page__state biz-lh-user-participation-page__state--loading'>
					{t('LAXHJALPEN.USER_PARTICIPATION.LOADING', { defaultValue: 'Laddar deltagarstatistik...' })}
				</div>
			)}

			{statusState === 'ERROR' && (
				<div className='biz-lh-user-participation-page__state biz-lh-user-participation-page__state--error'>
					{t('LAXHJALPEN.USER_PARTICIPATION.ERROR', {
						defaultValue: 'Det gick inte att hämta deltagarstatistiken.',
					})}
				</div>
			)}

			{statusState !== 'ERROR' && (
				<>
					<div className='biz-lh-user-participation-page__cards'>
						<SummaryCard
							accent='attendance'
							label={t('LAXHJALPEN.USER_PARTICIPATION.TOTAL_ATTENDANCE', { defaultValue: 'Total närvaro' })}
							value={`${summary.attendancePercent}%`}
							subValue={`${summary.uniqueEvents} ${t('LAXHJALPEN.USER_PARTICIPATION.PASSES', { defaultValue: 'pass' })}`}
						/>
						<SummaryCard
							accent='approved'
							label={t('LAXHJALPEN.USER_PARTICIPATION.APPROVED_ABSENCE', { defaultValue: 'Giltig frånvaro' })}
							value={`${summary.approvedAbsencePercent}%`}
							subValue={`${summary.uniqueEventsWApprovedAbsence} ${t('LAXHJALPEN.USER_PARTICIPATION.PASSES', { defaultValue: 'pass' })}`}
						/>
						<SummaryCard
							accent='not-approved'
							label={t('LAXHJALPEN.USER_PARTICIPATION.NOT_APPROVED_ABSENCE', { defaultValue: 'Ogiltig frånvaro' })}
							value={`${summary.notApprovedAbsencePercent}%`}
							subValue={`${summary.uniqueEventsWNotApprovedAbsence} ${t('LAXHJALPEN.USER_PARTICIPATION.PASSES', { defaultValue: 'pass' })}`}
						/>
						<SummaryCard
							accent='passes'
							label={t('LAXHJALPEN.USER_PARTICIPATION.COMPLETED_PASSES', { defaultValue: 'Genomförda pass' })}
							value={String(summary.uniqueEvents)}
							subValue={`${summary.usersWithData} ${t('LAXHJALPEN.USER_PARTICIPATION.USERS', { defaultValue: 'elever' })}`}
						/>
					</div>

					<div className='biz-lh-user-participation-page__overview'>
						<div className='biz-lh-user-participation-page__overview-header'>
							<div>
								<div className='biz-lh-user-participation-page__overview-title-row'>
									<h2>{t('LAXHJALPEN.USER_PARTICIPATION.OVERVIEW', { defaultValue: 'Översikt' })}</h2>
									<Tooltip title={overviewHelpContent} arrow placement='top'>
										<button
											type='button'
											className='biz-lh-user-participation-page__help-button'
											aria-label={t('LAXHJALPEN.USER_PARTICIPATION.HELP.ARIA_LABEL', {
												defaultValue: 'Visa hur översikten räknas ut',
											})}
										>
											<i className='fa-solid fa-info-circle' aria-hidden='true' />
										</button>
									</Tooltip>
								</div>
								<p>
									{t('LAXHJALPEN.USER_PARTICIPATION.OVERVIEW_DESCRIPTION', {
										defaultValue: 'Summering för vald period och enhet.',
									})}
								</p>
							</div>
							<span className='biz-lh-user-participation-page__updated'>
								{t('LAXHJALPEN.USER_PARTICIPATION.LAST_UPDATED', {
									defaultValue: `Senast uppdaterad ${lastUpdatedLabel}`,
									value: lastUpdatedLabel,
								})}
							</span>
						</div>

						<div className='biz-lh-user-participation-page__overview-content'>
							<div className='biz-lh-user-participation-page__donut-panel'>
								<div
									className='biz-lh-user-participation-page__donut'
									style={{
										background: buildDonutGradient(summary),
									}}
								>
									<div className='biz-lh-user-participation-page__donut-center'>
										<strong>{summary.attendancePercent}%</strong>
										<span>{t('LAXHJALPEN.USER_PARTICIPATION.ATTENDANCE', { defaultValue: 'Närvaro' })}</span>
									</div>
								</div>
							</div>

							<div className='biz-lh-user-participation-page__distribution'>
								<div className='biz-lh-user-participation-page__distribution-bar'>
									<div
										className='biz-lh-user-participation-page__distribution-segment biz-lh-user-participation-page__distribution-segment--attendance'
										style={{ width: `${summary.attendancePercent}%` }}
									/>
									<div
										className='biz-lh-user-participation-page__distribution-segment biz-lh-user-participation-page__distribution-segment--approved'
										style={{ width: `${summary.approvedAbsencePercent}%` }}
									/>
									<div
										className='biz-lh-user-participation-page__distribution-segment biz-lh-user-participation-page__distribution-segment--not-approved'
										style={{ width: `${summary.notApprovedAbsencePercent}%` }}
									/>
								</div>
								<div className='biz-lh-user-participation-page__legend'>
									<LegendItem
										accent='attendance'
										label={t('LAXHJALPEN.USER_PARTICIPATION.ATTENDANCE', { defaultValue: 'Närvaro' })}
										value={`${summary.attendancePercent}%`}
									/>
									<LegendItem
										accent='approved'
										label={t('LAXHJALPEN.USER_PARTICIPATION.APPROVED_ABSENCE', {
											defaultValue: 'Giltig frånvaro',
										})}
										value={`${summary.approvedAbsencePercent}%`}
									/>
									<LegendItem
										accent='not-approved'
										label={t('LAXHJALPEN.USER_PARTICIPATION.NOT_APPROVED_ABSENCE', {
											defaultValue: 'Ogiltig frånvaro',
										})}
										value={`${summary.notApprovedAbsencePercent}%`}
									/>
								</div>
							</div>
						</div>
					</div>

					<div className='biz-lh-user-participation-page__table-card'>
						{rows.length === 0 ? (
							<div className='biz-lh-user-participation-page__empty'>
								{t('LAXHJALPEN.USER_PARTICIPATION.EMPTY', {
									defaultValue: 'Ingen statistik hittades för det valda urvalet.',
								})}
							</div>
						) : (
							<table className='biz-lh-user-participation-page__table'>
								<thead>
									<tr>
										<th>#</th>
										<th>{t('LAXHJALPEN.USER_PARTICIPATION.USER', { defaultValue: 'Elev' })}</th>
										<th>{t('LAXHJALPEN.USER_PARTICIPATION.ATTENDANCE', { defaultValue: 'Närvaro' })}</th>
										<th>
											{t('LAXHJALPEN.USER_PARTICIPATION.APPROVED_ABSENCE', {
												defaultValue: 'Giltig frånvaro',
											})}
										</th>
										<th>
											{t('LAXHJALPEN.USER_PARTICIPATION.NOT_APPROVED_ABSENCE', {
												defaultValue: 'Ogiltig frånvaro',
											})}
										</th>
										<th>{t('LAXHJALPEN.USER_PARTICIPATION.PASSES', { defaultValue: 'Pass' })}</th>
										<th>{t('LAXHJALPEN.USER_PARTICIPATION.STATUS', { defaultValue: 'Status' })}</th>
									</tr>
								</thead>
								<tbody>
									{rows.map((row, index) => (
										<tr key={row.userId}>
											<td>{index + 1}</td>
											<td>{row.userName}</td>
											<td>{formatCellValue(row.attendancePercent, row.attendedCount)}</td>
											<td>{formatCellValue(row.approvedAbsencePercent, row.approvedAbsenceCount)}</td>
											<td>{formatCellValue(row.notApprovedAbsencePercent, row.notApprovedAbsenceCount)}</td>
											<td>{row.eventCount}</td>
											<td>
												<StatusPill status={row.status} />
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</>
			)}
		</div>
	);

	return isAdminContext ? adminPageWrapperMarkup(pageContent) : pageContent;
};

export default LaxhjalpenUserParticipationPage;

function SummaryCard({
	accent,
	label,
	value,
	subValue,
}: {
	accent: 'attendance' | 'approved' | 'not-approved' | 'passes';
	label: string;
	value: string;
	subValue: string;
}) {
	return (
		<div className={`biz-lh-user-participation-page__card biz-lh-user-participation-page__card--${accent}`}>
			<span className='biz-lh-user-participation-page__card-label'>{label}</span>
			<strong className='biz-lh-user-participation-page__card-value'>{value}</strong>
			<span className='biz-lh-user-participation-page__card-subvalue'>{subValue}</span>
		</div>
	);
}

function LegendItem({
	accent,
	label,
	value,
}: {
	accent: 'attendance' | 'approved' | 'not-approved';
	label: string;
	value: string;
}) {
	return (
		<div className='biz-lh-user-participation-page__legend-item'>
			<span
				className={`biz-lh-user-participation-page__legend-dot biz-lh-user-participation-page__legend-dot--${accent}`}
			/>
			<span className='biz-lh-user-participation-page__legend-label'>{label}</span>
			<span className='biz-lh-user-participation-page__legend-value'>{value}</span>
		</div>
	);
}

function StatusPill({ status }: { status: UserRow['status'] }) {
	const { t } = useTranslation();
	const labelByStatus: Record<UserRow['status'], string> = {
		high: t('LAXHJALPEN.USER_PARTICIPATION.STATUS_HIGH', { defaultValue: 'Hög närvaro' }),
		stable: t('LAXHJALPEN.USER_PARTICIPATION.STATUS_STABLE', { defaultValue: 'Stabil' }),
		'follow-up': t('LAXHJALPEN.USER_PARTICIPATION.STATUS_FOLLOW_UP', { defaultValue: 'Behöver följas upp' }),
	};

	return (
		<span className={`biz-lh-user-participation-page__status biz-lh-user-participation-page__status--${status}`}>
			{labelByStatus[status]}
		</span>
	);
}

function describePeriod(period: DashboardPeriod) {
	const { from, to } = getDateRangeForPeriod(period);
	return `${formatToSimpleDateString(from)} - ${formatToSimpleDateString(to)}`;
}

function formatDateRange(from: Date, to: Date) {
	return `${formatToSimpleDateString(from)} - ${formatToSimpleDateString(to)}`;
}

function formatCellValue(percent: number, count: number) {
	return `${percent}% (${count})`;
}

function mapRow(row: LhUserParticipationStats): UserRow {
	const attendancePercent = getPercent(row.attendedCount, row.eventCount);
	const approvedAbsencePercent = getPercent(row.approvedAbsenceCount, row.eventCount);
	const notApprovedAbsencePercent = getPercent(row.notApprovedAbsenceCount, row.eventCount);

	let status: UserRow['status'] = 'follow-up';
	if (attendancePercent > 95) {
		status = 'high';
	} else if (attendancePercent >= 90) {
		status = 'stable';
	}

	return {
		...row,
		attendancePercent,
		approvedAbsencePercent,
		notApprovedAbsencePercent,
		status,
	};
}

function summarizeRows(
	rows: LhUserParticipationStats[],
	uniqueEvents: number,
	uniqueEventsWApprovedAbsence: number,
	uniqueEventsWNotApprovedAbsence: number
): ParticipationSummary {
	const totalParticipations = rows.reduce((sum, row) => sum + row.eventCount, 0);
	const attendedCount = rows.reduce((sum, row) => sum + row.attendedCount, 0);
	const approvedAbsenceCount = rows.reduce((sum, row) => sum + row.approvedAbsenceCount, 0);
	const notApprovedAbsenceCount = rows.reduce((sum, row) => sum + row.notApprovedAbsenceCount, 0);

	return {
		totalParticipations,
		attendedCount,
		approvedAbsenceCount,
		notApprovedAbsenceCount,
		attendancePercent: getPercent(attendedCount, totalParticipations),
		approvedAbsencePercent: getPercent(approvedAbsenceCount, totalParticipations),
		notApprovedAbsencePercent: getPercent(notApprovedAbsenceCount, totalParticipations),
		uniqueEvents,
		uniqueEventsWApprovedAbsence,
		uniqueEventsWNotApprovedAbsence,
		usersWithData: rows.length,
	};
}

function getPercent(value: number, total: number) {
	if (total <= 0) {
		return 0;
	}

	return Math.round((value / total) * 100);
}

function buildDonutGradient(summary: ParticipationSummary) {
	const attendance = summary.attendancePercent;
	const approvedEnd = attendance + summary.approvedAbsencePercent;
	const notApprovedEnd = approvedEnd + summary.notApprovedAbsencePercent;

	return `conic-gradient(#34c759 0% ${attendance}%, #3aa0ff ${attendance}% ${approvedEnd}%, #ff5b5b ${approvedEnd}% ${notApprovedEnd}%, #e9eef5 ${notApprovedEnd}% 100%)`;
}

function getLastUpdatedLabel(lastUpdated: string | null, t: ReturnType<typeof useTranslation>['t']) {
	if (!lastUpdated) {
		return t('LAXHJALPEN.USER_PARTICIPATION.LAST_UPDATED_UNKNOWN', { defaultValue: 'okänd tidpunkt' });
	}

	try {
		return formatToTodayString(parseStringToDate(lastUpdated));
	} catch {
		return lastUpdated;
	}
}
